import React, { useEffect, useMemo, useState, useCallback } from "react";
import { colors } from '../../theme/colors';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Collapse,
  Divider,
  Tooltip,
  Popover,
  MenuList,
  MenuItem,
  Box,
  Typography,
  tooltipClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

import EventIcon from "@mui/icons-material/Event";
import ListAltIcon from "@mui/icons-material/ListAlt";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import AssessmentIcon from "@mui/icons-material/Assessment";

import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PeopleIcon from "@mui/icons-material/People";
import WorkIcon from "@mui/icons-material/Work";
import QuizIcon from "@mui/icons-material/Quiz";

import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import PeopleOutlineOutlinedIcon from "@mui/icons-material/PeopleOutlineOutlined";
import PlagiarismIcon from "@mui/icons-material/Plagiarism";
import DescriptionIcon from "@mui/icons-material/Description";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import StarIcon from "@mui/icons-material/Star";

import logo from "../../assets/Images/element-wordmark-white.svg";
import { useLocation, useNavigate } from "react-router-dom";
import useGlobalContext from "../../contexts-hooks/hooks/useGlobalContext";
import { CustomDrawer, DrawerHeader } from "./style";

const SidebarTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: colors.primary,
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: 600,
    borderRadius: 8,
    padding: "6px 10px",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: colors.primary,
  },
}));

const NavItem = ({
  open,
  label,
  icon,
  isActive,
  onClick,
  endIcon = null,
  sx = {},
}) => {
  return (
    <SidebarTooltip
      title={!open ? label : ""}
      placement="right"
      disableHoverListener={open}
    >
      <ListItemButton
        onClick={onClick}
        className={isActive ? "active-tab" : ""}
        sx={[
          { justifyContent: open ? "flex-start" : "center", px: 2 },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: open ? 2 : "auto",
            justifyContent: "center",
          }}
        >
          {icon}
        </ListItemIcon>

        {open && <ListItemText primary={label} sx={{ flex: 1 }} />}
        {open && (
          <Box
            sx={{
              width: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {endIcon}
          </Box>
        )}
      </ListItemButton>
    </SidebarTooltip>
  );
};

const CollapsedMenu = ({
  open,
  menuKey,
  openMenuKey,
  anchorEl,
  onClose,
  items,
  activeTab,
  onNavigate,
  minWidth = 240,
}) => {
  const isOpen = Boolean(anchorEl) && openMenuKey === menuKey;

  return (
    <Popover
      open={!open && isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "center", horizontal: "right" }}
      transformOrigin={{ vertical: "center", horizontal: "left" }}
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
    >
      <MenuList sx={{ minWidth }}>
        {items.map((item) => (
          <MenuItem
            key={item.key}
            onClick={() => onNavigate(item.path)}
            selected={activeTab === item.key}
          >
            <ListItemIcon sx={{ minWidth: 32, color: colors.primary }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </MenuItem>
        ))}
      </MenuList>
    </Popover>
  );
};

const Sidebar = ({ open, setOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = useGlobalContext();

  const userType = state?.user?.role || "";

  const [activeTab, setActiveTab] = useState("");
  const [employeesOpen, setEmployeesOpen] = useState(false);
  const [settingOpen, setSettingOpen] = useState(false);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [openMenuKey, setOpenMenuKey] = useState(null);

  useEffect(() => {
    const currentPath = location.pathname.split("/").pop();
    setActiveTab(currentPath || "");
  }, [location.pathname]);

  useEffect(() => {
    setMenuAnchor(null);
    setOpenMenuKey(null);
  }, [open]);

  const onNavigate = useCallback(
    (path) => {
      const tab = path.split("/").pop();
      navigate(path);
      setActiveTab(tab || "");
      setMenuAnchor(null);
      setOpenMenuKey(null);
    },
    [navigate],
  );

  const toggleDrawer = useCallback(() => {
    if (open) {
      setEmployeesOpen(false);
      setSettingOpen(false);
    }
    setOpen(!open);
  }, [open, setOpen]);

  const openCollapsedMenu = useCallback((event, key) => {
    setMenuAnchor(event.currentTarget);
    setOpenMenuKey(key);
  }, []);

  const closeCollapsedMenu = useCallback(() => {
    setMenuAnchor(null);
    setOpenMenuKey(null);
  }, []);

  const navItems = useMemo(
    () => [
      {
        key: "jobs",
        label: "Jobs",
        icon: <WorkIcon />,
        path: "/app/jobs",
        visible: true,
      },
      {
        key: "global-talent-search",
        label: "Global Talent Search",
        icon: <SearchIcon />,
        path: "/app/global-talent-search",
        visible: true,
      },
      {
        key: "intelligent-resume-matching",
        label: "Intelligent Resume Matching",
        icon: <PlagiarismIcon />,
        path: "/app/intelligent-resume-matching",
        visible: true,
      },
      {
        key: "create-job-description",
        label: "Jobs Description",
        icon: <DescriptionIcon />,
        path: "/app/create-job-description",
        visible: true,
      },
      {
        key: "selected",
        label: "Selected",
        icon: <BookmarkIcon />,
        path: "/app/selected",
        visible: true,
      },
    ],
    [],
  );

  const interviewSubItems = useMemo(
    () => [
      {
        key: "schedule-interview",
        label: "Schedule Interview",
        path: "/app/interview/schedule-interview",
        icon: <EventIcon />,
      },
      {
        key: "list-schedule-interview",
        label: "List of Schedule Interview",
        path: "/app/interview/list-schedule-interview",
        icon: <ListAltIcon />,
      },
      {
        key: "edit-schedule-interview",
        label: "Edit Schedule Interview",
        path: "/app/interview/edit-schedule-interview",
        icon: <EditCalendarIcon />,
      },
      {
        key: "candidate-reports",
        label: "Candidate Report",
        path: "/app/interview/candidate-reports",
        icon: <AssessmentIcon />,
      },
    ],
    [],
  );

  const settingsSubItems = useMemo(
    () => [
      {
        key: "create-recruiter",
        label: "Create Recruiter Credentials",
        path: "/app/settings/create-recruiter",
        icon: <PersonAddIcon />,
      },
      {
        key: "recruiter-list",
        label: "List Of Recruiters",
        path: "/app/settings/recruiter-list",
        icon: <PeopleIcon />,
      },
      {
        key: "create-role",
        label: "Create Role/Company-Client",
        path: "/app/settings/create-role",
        icon: <WorkIcon />,
      },
      {
        key: "create-custom-question",
        label: "Create Custom Question",
        path: "/app/settings/create-custom-question",
        icon: <QuizIcon />,
      },
    ],
    [],
  );

  const showInterview = userType === "admin" || userType === "recruiter";
  const showSettings = userType === "admin";

  return (
    <CustomDrawer variant="permanent" open={open}>
      <DrawerHeader open={open}>
        {open ? <img className="logo" src={logo} alt="Berlin" /> : <Box />}
        <IconButton
          onClick={toggleDrawer}
          sx={{ "&:hover": { backgroundColor: "transparent" } }}
        >
          {open ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
      </DrawerHeader>

      <List>
        {navItems
          .filter((i) => i.visible)
          .map((item) => (
            <NavItem
              key={item.key}
              open={open}
              label={item.label}
              icon={item.icon}
              isActive={activeTab === item.key}
              onClick={() => onNavigate(item.path)}
            />
          ))}

        {showInterview && (
          <>
            <Divider className="nav-divider" />
            {open && (
              <Typography component="span" className="nav-section-label">
                Interview
              </Typography>
            )}
            <NavItem
              open={open}
              label="Contextual AI Interview"
              icon={<PeopleOutlineOutlinedIcon />}
              isActive={false}
              endIcon={employeesOpen ? <ExpandLess /> : <ExpandMore />}
              onClick={(e) => {
                if (!open) openCollapsedMenu(e, "interview");
                else setEmployeesOpen((p) => !p);
              }}
            />

            {open && (
              <Collapse in={employeesOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 1 }}>
                  {interviewSubItems.map((sub) => (
                    <ListItemButton
                      key={sub.key}
                      onClick={() => onNavigate(sub.path)}
                      className={`sub-item${activeTab === sub.key ? " active-tab" : ""}`}
                      sx={{ py: 0.6, px: 2, marginLeft: 0, marginRight: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>{sub.icon}</ListItemIcon>
                      <ListItemText primary={sub.label} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </>
        )}

        {showSettings && (
          <NavItem
            open={open}
            label="Final Shortlist"
            icon={<StarIcon />}
            isActive={activeTab === "final-shortlisted"}
            onClick={() => onNavigate("/app/interview/final-shortlisted")}
          />
        )}

        {showSettings && (
          <>
            <Divider className="nav-divider" />
            {open && (
              <Typography component="span" className="nav-section-label">
                Settings
              </Typography>
            )}
            <NavItem
              open={open}
              label="Settings"
              icon={<SettingsIcon />}
              isActive={false}
              endIcon={settingOpen ? <ExpandLess /> : <ExpandMore />}
              onClick={(e) => {
                if (!open) openCollapsedMenu(e, "settings");
                else setSettingOpen((p) => !p);
              }}
            />

            {open && (
              <Collapse in={settingOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 1 }}>
                  {settingsSubItems.map((sub) => (
                    <ListItemButton
                      key={sub.key}
                      onClick={() => onNavigate(sub.path)}
                      className={`sub-item${activeTab === sub.key ? " active-tab" : ""}`}
                      sx={{ py: 0.6, px: 2, marginLeft: 0, marginRight: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>{sub.icon}</ListItemIcon>
                      <ListItemText primary={sub.label} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </>
        )}
      </List>

      <CollapsedMenu
        open={open}
        menuKey="interview"
        openMenuKey={openMenuKey}
        anchorEl={menuAnchor}
        onClose={closeCollapsedMenu}
        items={interviewSubItems}
        activeTab={activeTab}
        onNavigate={onNavigate}
        minWidth={240}
      />

      <CollapsedMenu
        open={open}
        menuKey="settings"
        openMenuKey={openMenuKey}
        anchorEl={menuAnchor}
        onClose={closeCollapsedMenu}
        items={settingsSubItems}
        activeTab={activeTab}
        onNavigate={onNavigate}
        minWidth={260}
      />
    </CustomDrawer>
  );
};

export default Sidebar;
